import { computed, inject, Injectable, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { LocalStorageService } from '@core/common-legacy';
import { SuggestAiActions, SuggestAiSelectors } from '@core/email-ai-data';
import {
  EmailAiConfirmDialogResult,
  EmailAiTypes,
  SuggestAiEntitiesEnum,
} from '@core/email-ai-types';
import { EmailAiConfirmDialogComponent } from '@core/email-ai-ui';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { EmailAiAnalyticsService } from './email-ai-analytics.service';
import { EmailAiGeneratorService } from './email-ai-generator.service';

@Injectable({
  providedIn: 'root',
})
export class EmailAiBannerService {
  private readonly ls = inject(LocalStorageService);
  private readonly dialogService = inject(DialogService);
  private readonly emailAiGenerator = inject(EmailAiGeneratorService);
  private readonly emailAiAnalytics = inject(EmailAiAnalyticsService);
  private readonly store = inject(Store);

  private readonly LS_KEY = 'hideRejectionBanner';

  public readonly isEmailAiEnabled = this.emailAiGenerator.isEmailAiEnabled;

  public showAiPrompt = this.emailAiGenerator.isAiPromptVisible;

  public topic = signal<EmailAiTypes | null>(null);

  public showRejectionBanner = computed(
    () =>
      this.isEmailAiEnabled() &&
      !this.showAiPrompt() &&
      !this.rejectionBannerVisible &&
      !this.hideBanner() &&
      this.suggestionFound(),
  );

  public showRejectionBannerInWalkthrough = computed(
    () => this.isEmailAiEnabled() && !this.showAiPrompt(),
  );

  public templateBannerVisible = signal(false);
  private hideBanner = signal(false);
  private suggestion = this.store.selectSignal(SuggestAiSelectors.data);
  private suggestionFound = computed(
    () =>
      !!this.suggestion()?.topics.length &&
      this.suggestion()?.topics.findIndex(e => e === SuggestAiEntitiesEnum.RejectionEmail) !== -1,
  );

  public fetchAiDisplaySuggestions(candidateId: number) {
    this.store.dispatch(
      SuggestAiActions.fetchAiDisplaySuggestions({ payload: { id: candidateId } }),
    );
  }

  public closeRejectionBanner() {
    return this.dialogService
      .openCustom(EmailAiConfirmDialogComponent, {
        width: DialogWidth.SmallXX,
      })
      .afterClosed.subscribe((result: EmailAiConfirmDialogResult) => {
        this.hideBanner.set(result.confirmed);
        this.ls.set(this.LS_KEY, result.payload.dontShowAgain);
        this.emailAiAnalytics.trackRejectionBannerDismissed(result.payload.dontShowAgain);
      });
  }

  public hideRejectionBanner(trackEvent: boolean) {
    if (trackEvent) this.emailAiAnalytics.trackRejectionBannerDismissed(false);
    this.hideBanner.set(true);
  }

  public showTemplateBanner() {
    this.templateBannerVisible.set(true);
  }

  public hideTemplateBanner() {
    this.templateBannerVisible.set(false);
  }

  private get rejectionBannerVisible() {
    return !!this.ls.get(this.LS_KEY);
  }
}
